// src/Context/CartContext.js
import React, { createContext, useContext, useEffect, useMemo, useState } from "react";
import {
  getMyCart,
  addToCart as apiAddToCart,
  updateCartItem as apiUpdateCartItem,
  deleteCartItem as apiDeleteCartItem,
  clearCart as apiClearCart,
} from "../services/UserApi/CartApi"; // ✅ عدّل المسار لو مختلف عندك
// order API - عدّل المسار/الاسم لو ملفك مختلف
import { makeOrder as apiMakeOrder } from "../services/UserApi/OrderApi";

const CartContext = createContext(null);

const getToken = () =>
  localStorage.getItem("token") || sessionStorage.getItem("token") || null;

export const CartProvider = ({ children }) => {
  const [cart, setCart] = useState(null); // نخزن response كامل
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // ✅ يتغير مع أي تغيير في token (عن طريق event listener بسيط)
  const [tokenState, setTokenState] = useState(getToken());

  // لو عندك login/logout بيكتب token في localStorage/sessionStorage
  // تقدر تنادي window.dispatchEvent(new Event("auth-token-changed"))
  // وأنا هنا عامل fallback polling خفيف كل 1 ثانية (ممكن تشيله لو هتعمل event)
  useEffect(() => {
    const onTokenChanged = () => setTokenState(getToken());
    window.addEventListener("auth-token-changed", onTokenChanged);

    const interval = setInterval(() => {
      const t = getToken();
      setTokenState((prev) => (prev === t ? prev : t));
    }, 1000);

    return () => {
      window.removeEventListener("auth-token-changed", onTokenChanged);
      clearInterval(interval);
    };
  }, []);

  const isAuthed = Boolean(tokenState);

  const extractMsg = (e, fallback) =>
    e?.response?.data?.message_ar ||
    e?.response?.data?.message ||
    e?.message ||
    fallback;

  // ✅ جلب السلة
  const refreshCart = async () => {
    if (!isAuthed) {
      setCart(null);
      return null;
    }

    setLoading(true);
    setError("");
    try {
      const data = await getMyCart();
      setCart(data);
      return data;
    } catch (e) {
      const msg = extractMsg(e, "Failed to load cart");
      setError(msg);
      throw e;
    } finally {
      setLoading(false);
    }
  };

  // ✅ إضافة للسلة
  const addItem = async ({ product_id, size_id = null, color_id = null, quantity = 1 }) => {
    if (!product_id) throw new Error("product_id is required");

    setLoading(true);
    setError("");
    try {
      const res = await apiAddToCart({ product_id, size_id, color_id, quantity });
      await refreshCart();
      return res;
    } catch (e) {
      const msg = extractMsg(e, "Failed to add to cart");
      setError(msg);
      throw e;
    } finally {
      setLoading(false);
    }
  };

  // ✅ تحديث كمية عنصر
  const updateItem = async ({ item_id, quantity }) => {
    if (!item_id) throw new Error("item_id is required");

    setLoading(true);
    setError("");
    try {
      const res = await apiUpdateCartItem({ item_id, quantity });
      await refreshCart();
      return res;
    } catch (e) {
      const msg = extractMsg(e, "Failed to update cart item");
      setError(msg);
      throw e;
    } finally {
      setLoading(false);
    }
  };

  // ✅ حذف عنصر
  const removeItem = async ({ item_id }) => {
    if (!item_id) throw new Error("item_id is required");

    setLoading(true);
    setError("");
    try {
      const res = await apiDeleteCartItem({ item_id });
      await refreshCart();
      return res;
    } catch (e) {
      const msg = extractMsg(e, "Failed to delete cart item");
      setError(msg);
      throw e;
    } finally {
      setLoading(false);
    }
  };

  // ✅ تفريغ السلة
  const clear = async () => {
    setLoading(true);
    setError("");
    try {
      const res = await apiClearCart();
      await refreshCart();
      return res;
    } catch (e) {
      const msg = extractMsg(e, "Failed to clear cart");
      setError(msg);
      throw e;
    } finally {
      setLoading(false);
    }
  };

  // ✅ إنشاء طلب (make order)
  const makeOrder = async (orderData) => {
    // orderData example:
    // { payment_method, phone, country_id, governorate_id, city_id, address, postal_code, notes, shipping_cost, discount }
    setLoading(true);
    setError("");
    try {
      const res = await apiMakeOrder(orderData);
      // بعد إنشاء الطلب ممكن نحدّث السلة
      await refreshCart();
      return res;
    } catch (e) {
      const msg = extractMsg(e, "Failed to create order");
      setError(msg);
      throw e;
    } finally {
      setLoading(false);
    }
  };

  // ✅ لما يبقى فيه token اعمل refresh
  useEffect(() => {
    if (isAuthed) refreshCart();
    else setCart(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthed]);

  const value = useMemo(
    () => ({
      // state
      cart,
      loading,
      error,

      // core actions
      refreshCart,
      addItem,
      updateItem,
      removeItem,
      clear,
      makeOrder,
      setCart,

      // ✅ aliases (عشان أي كومبوننت قديم يشتغل)
      addToCart: addItem,
      updateCartItem: updateItem,
      deleteCartItem: removeItem,
      clearCart: clear,
      createOrder: makeOrder,
    }),
    [cart, loading, error]
  );

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
};

export const useCart = () => {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error("useCart must be used within a CartProvider");
  return ctx;
};
